import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { apiService } from '../services/api';
import { getTodayInUserTimezone } from '../utils/dateUtils';

interface MoveDayDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const formatDateForCompare = (d: string | Date | null | undefined): string | null => {
  if (!d) return null;
  const date = typeof d === 'string' ? new Date(d) : d;
  return date.toISOString().slice(0, 10);
};

const MoveDayDialog: React.FC<MoveDayDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [fromDate, setFromDate] = useState(getTodayInUserTimezone());
  const [toDate, setToDate] = useState(getTodayInUserTimezone());
  const [serviceCount, setServiceCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setFromDate(getTodayInUserTimezone());
      setToDate(getTodayInUserTimezone());
      setServiceCount(0);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !fromDate) {
      setServiceCount(0);
      return;
    }
    const fetchCount = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.get(
          '/service-requests?userType=provider&status=accepted,scheduled,in-progress,completed&limit=500'
        );
        if (!res.success || !res.data) {
          setServiceCount(0);
          return;
        }
        const requests = (res.data as any).serviceRequests || [];
        const fromStr = formatDateForCompare(fromDate) || fromDate;
        const count = requests.filter((sr: any) => {
          const srDateStr = formatDateForCompare(sr.scheduledDate);
          return srDateStr === fromStr;
        }).length;
        setServiceCount(count);
      } catch (err: any) {
        setError(err.message || 'Failed to load services');
        setServiceCount(0);
      } finally {
        setLoading(false);
      }
    };
    fetchCount();
  }, [open, fromDate]);

  const handleSubmit = async () => {
    if (fromDate === toDate) {
      setError('From date and to date must be different');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiService.post('/service-requests/move-day', {
        fromDate,
        toDate,
      });
      if (res.success && res.data) {
        const { successCount, failCount } = res.data as any;
        const msg = failCount > 0
          ? `Moved ${successCount} services. ${failCount} failed.`
          : `Successfully moved ${successCount} services to ${toDate}.`;
        onSuccess();
        onClose();
        alert(msg);
      } else {
        setError((res as any).error || 'Move day failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'Move day failed');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = fromDate && toDate && fromDate !== toDate && !loading;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Move Day (Weather Reschedule)</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Move all services from one date to another. Use this for weather delays—all moves are one-time changes.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="From Date"
            type="date"
            fullWidth
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To Date"
            type="date"
            fullWidth
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Counting services...</Typography>
            </Box>
          ) : (
            <Typography variant="body2">
              {serviceCount} service{serviceCount !== 1 ? 's' : ''} on {fromDate} will be moved to {toDate}.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit || serviceCount === 0}
        >
          {submitting ? <CircularProgress size={24} /> : 'Move Day'}
        </Button>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MoveDayDialog;
